import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Building2,
  FileQuestion,
  Library,
  Radio,
  Users,
  type LucideIcon,
} from 'lucide-react'
import { adminKeys, type AdminAnalytics } from './types'

/**
 * The admin overview's stat-card grid. Counts come from
 * `GET /api/v1/admin/analytics` (all platform-wide aggregates). The
 * live-sessions card is visually distinct (accent border + pulsing dot) and
 * the whole query auto-refreshes every 10s so the live count stays current;
 * its number is wrapped in an `aria-live="polite"` region so screen-reader
 * users hear updates without a manual refresh.
 */
export function AnalyticsCards() {
  const { t } = useTranslation('admin')

  const analyticsQuery = useQuery({
    queryKey: adminKeys.analytics,
    queryFn: async ({ signal }) => {
      const res = await fetch('/api/v1/admin/analytics', {
        credentials: 'include',
        signal,
      })
      if (!res.ok) {
        throw new Error(`Failed to load analytics: ${res.status}`)
      }
      return (await res.json()) as AdminAnalytics
    },
    // Keep the live-session count current without a manual refresh.
    refetchInterval: 10_000,
  })

  if (analyticsQuery.isLoading) {
    return <AnalyticsSkeleton />
  }

  if (analyticsQuery.isError || !analyticsQuery.data) {
    return (
      <div
        className="rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3"
        role="alert"
      >
        {t('loadError')}
      </div>
    )
  }

  const a = analyticsQuery.data

  return (
    <ul
      role="list"
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
    >
      {/* Live sessions — distinct accent + pulsing dot + aria-live count. */}
      <li>
        <article className="relative flex h-full flex-col rounded-2xl border border-emerald-200 bg-white border-s-4 border-s-emerald-500 ps-5 pe-5 py-5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
              <Radio className="h-5 w-5" aria-hidden="true" />
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700">
              <span
                className="inline-block h-2 w-2 rounded-full bg-emerald-500 animate-pulse"
                aria-hidden="true"
              />
              {t('live')}
            </span>
          </div>
          <p
            className="mt-4 text-3xl font-bold font-display text-brand-900 tabular-nums"
            aria-live="polite"
          >
            <span dir="ltr">{a.liveSessions}</span>
          </p>
          <p className="mt-1 text-sm font-medium text-brand-600">
            {t('liveSessions')}
          </p>
        </article>
      </li>

      <StatCard icon={Users} label={t('totalUsers')} value={a.totalUsers} />
      <StatCard
        icon={Building2}
        label={t('totalOrganizations')}
        value={a.totalOrganizations}
      />
      <StatCard
        icon={FileQuestion}
        label={t('totalQuizzes')}
        value={a.totalQuizzes}
      />

      {/* Bank items — spans the row on lg with a public/private split. */}
      <li className="sm:col-span-2 lg:col-span-1">
        <article className="flex h-full flex-col rounded-2xl border border-brand-100 bg-white border-s-4 border-s-brand-500 ps-5 pe-5 py-5 shadow-sm">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
            <Library className="h-5 w-5" aria-hidden="true" />
          </span>
          <p className="mt-4 text-3xl font-bold font-display text-brand-900 tabular-nums">
            <span dir="ltr">{a.totalBankItems}</span>
          </p>
          <p className="mt-1 text-sm font-medium text-brand-600">
            {t('totalBankItems')}
          </p>
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-1 font-semibold text-brand-700">
              {t('publicItems')}
              <span dir="ltr" className="tabular-nums">
                {a.publicBankItems}
              </span>
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-ink-50 px-2.5 py-1 font-semibold text-brand-700">
              {t('privateItems')}
              <span dir="ltr" className="tabular-nums">
                {a.privateBankItems}
              </span>
            </span>
          </div>
        </article>
      </li>
    </ul>
  )
}

type StatCardProps = {
  icon: LucideIcon
  label: string
  value: number
}

function StatCard({ icon: Icon, label, value }: StatCardProps) {
  return (
    <li>
      <article className="flex h-full flex-col rounded-2xl border border-brand-100 bg-white border-s-4 border-s-brand-500 ps-5 pe-5 py-5 shadow-sm">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
        <p className="mt-4 text-3xl font-bold font-display text-brand-900 tabular-nums">
          <span dir="ltr">{value}</span>
        </p>
        <p className="mt-1 text-sm font-medium text-brand-600">{label}</p>
      </article>
    </li>
  )
}

function AnalyticsSkeleton() {
  return (
    <ul
      role="list"
      aria-hidden="true"
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
    >
      {Array.from({ length: 5 }).map((_, i) => (
        <li
          key={i}
          className="h-36 rounded-2xl border border-brand-100 bg-white border-s-4 border-s-brand-100 ps-5 pe-5 py-5 animate-pulse"
        >
          <div className="h-10 w-10 rounded-xl bg-brand-100" />
          <div className="mt-4 h-8 w-16 rounded bg-brand-100" />
          <div className="mt-2 h-3.5 w-24 rounded bg-brand-50" />
        </li>
      ))}
    </ul>
  )
}
